import { Vec3 } from './vec3';
import { Quat } from './quat';

/** Column-major 4x4 matrix (WebGL convention). */
export class Mat4 {
  readonly e: Float32Array;
  constructor(e?: Float32Array) {
    this.e = e ?? new Float32Array(16);
    if (!e) this.identity();
  }

  identity(): this {
    const e = this.e;
    e.fill(0); e[0] = e[5] = e[10] = e[15] = 1;
    return this;
  }
  copy(m: Mat4): this { this.e.set(m.e); return this; }
  clone(): Mat4 { return new Mat4(new Float32Array(this.e)); }

  /** this = a * b */
  multiplyMatrices(a: Mat4, b: Mat4): this {
    const ae = a.e, be = b.e, te = this.e;
    const a00 = ae[0], a01 = ae[1], a02 = ae[2], a03 = ae[3];
    const a10 = ae[4], a11 = ae[5], a12 = ae[6], a13 = ae[7];
    const a20 = ae[8], a21 = ae[9], a22 = ae[10], a23 = ae[11];
    const a30 = ae[12], a31 = ae[13], a32 = ae[14], a33 = ae[15];
    for (let i = 0; i < 4; i++) {
      const b0 = be[i * 4], b1 = be[i * 4 + 1], b2 = be[i * 4 + 2], b3 = be[i * 4 + 3];
      te[i * 4] = b0 * a00 + b1 * a10 + b2 * a20 + b3 * a30;
      te[i * 4 + 1] = b0 * a01 + b1 * a11 + b2 * a21 + b3 * a31;
      te[i * 4 + 2] = b0 * a02 + b1 * a12 + b2 * a22 + b3 * a32;
      te[i * 4 + 3] = b0 * a03 + b1 * a13 + b2 * a23 + b3 * a33;
    }
    return this;
  }
  multiply(m: Mat4): this { return this.multiplyMatrices(this.clone(), m); }
  premultiply(m: Mat4): this { return this.multiplyMatrices(m, this.clone()); }

  compose(p: Vec3, q: Quat, s: Vec3): this {
    const e = this.e;
    const x = q.x, y = q.y, z = q.z, w = q.w;
    const x2 = x + x, y2 = y + y, z2 = z + z;
    const xx = x * x2, xy = x * y2, xz = x * z2;
    const yy = y * y2, yz = y * z2, zz = z * z2;
    const wx = w * x2, wy = w * y2, wz = w * z2;
    e[0] = (1 - (yy + zz)) * s.x; e[1] = (xy + wz) * s.x; e[2] = (xz - wy) * s.x; e[3] = 0;
    e[4] = (xy - wz) * s.y; e[5] = (1 - (xx + zz)) * s.y; e[6] = (yz + wx) * s.y; e[7] = 0;
    e[8] = (xz + wy) * s.z; e[9] = (yz - wx) * s.z; e[10] = (1 - (xx + yy)) * s.z; e[11] = 0;
    e[12] = p.x; e[13] = p.y; e[14] = p.z; e[15] = 1;
    return this;
  }
  setTranslation(v: Vec3): this { this.identity(); this.e[12] = v.x; this.e[13] = v.y; this.e[14] = v.z; return this; }
  setScale(v: Vec3): this { this.identity(); this.e[0] = v.x; this.e[5] = v.y; this.e[10] = v.z; return this; }
  getTranslation(out = new Vec3()): Vec3 { return out.set(this.e[12], this.e[13], this.e[14]); }

  perspective(fovY: number, aspect: number, near: number, far: number): this {
    const f = 1 / Math.tan(fovY / 2), nf = 1 / (near - far);
    const e = this.e;
    e.fill(0);
    e[0] = f / aspect; e[5] = f; e[10] = (far + near) * nf; e[11] = -1; e[14] = 2 * far * near * nf;
    return this;
  }
  ortho(l: number, r: number, b: number, t: number, n: number, f: number): this {
    const e = this.e;
    e.fill(0);
    e[0] = 2 / (r - l); e[5] = 2 / (t - b); e[10] = -2 / (f - n);
    e[12] = -(r + l) / (r - l); e[13] = -(t + b) / (t - b); e[14] = -(f + n) / (f - n); e[15] = 1;
    return this;
  }
  lookAt(eye: Vec3, target: Vec3, up: Vec3): this {
    const z = Vec3.sub(eye, target).normalize();
    if (z.isZero()) z.z = 1;
    let x = Vec3.cross(up, z);
    if (x.lengthSq() < 1e-10) { z.x += 1e-4; x = Vec3.cross(up, z); }
    x.normalize();
    const y = Vec3.cross(z, x);
    const e = this.e;
    e[0] = x.x; e[1] = y.x; e[2] = z.x; e[3] = 0;
    e[4] = x.y; e[5] = y.y; e[6] = z.y; e[7] = 0;
    e[8] = x.z; e[9] = y.z; e[10] = z.z; e[11] = 0;
    e[12] = -x.dot(eye); e[13] = -y.dot(eye); e[14] = -z.dot(eye); e[15] = 1;
    return this;
  }

  invert(): this {
    const e = this.e;
    const n11 = e[0], n21 = e[1], n31 = e[2], n41 = e[3];
    const n12 = e[4], n22 = e[5], n32 = e[6], n42 = e[7];
    const n13 = e[8], n23 = e[9], n33 = e[10], n43 = e[11];
    const n14 = e[12], n24 = e[13], n34 = e[14], n44 = e[15];
    const t11 = n23 * n34 * n42 - n24 * n33 * n42 + n24 * n32 * n43 - n22 * n34 * n43 - n23 * n32 * n44 + n22 * n33 * n44;
    const t12 = n14 * n33 * n42 - n13 * n34 * n42 - n14 * n32 * n43 + n12 * n34 * n43 + n13 * n32 * n44 - n12 * n33 * n44;
    const t13 = n13 * n24 * n42 - n14 * n23 * n42 + n14 * n22 * n43 - n12 * n24 * n43 - n13 * n22 * n44 + n12 * n23 * n44;
    const t14 = n14 * n23 * n32 - n13 * n24 * n32 - n14 * n22 * n33 + n12 * n24 * n33 + n13 * n22 * n34 - n12 * n23 * n34;
    const det = n11 * t11 + n21 * t12 + n31 * t13 + n41 * t14;
    if (det === 0) return this.identity();
    const d = 1 / det;
    e[0] = t11 * d;
    e[1] = (n24 * n33 * n41 - n23 * n34 * n41 - n24 * n31 * n43 + n21 * n34 * n43 + n23 * n31 * n44 - n21 * n33 * n44) * d;
    e[2] = (n22 * n34 * n41 - n24 * n32 * n41 + n24 * n31 * n42 - n21 * n34 * n42 - n22 * n31 * n44 + n21 * n32 * n44) * d;
    e[3] = (n23 * n32 * n41 - n22 * n33 * n41 - n23 * n31 * n42 + n21 * n33 * n42 + n22 * n31 * n43 - n21 * n32 * n43) * d;
    e[4] = t12 * d;
    e[5] = (n13 * n34 * n41 - n14 * n33 * n41 + n14 * n31 * n43 - n11 * n34 * n43 - n13 * n31 * n44 + n11 * n33 * n44) * d;
    e[6] = (n14 * n32 * n41 - n12 * n34 * n41 - n14 * n31 * n42 + n11 * n34 * n42 + n12 * n31 * n44 - n11 * n32 * n44) * d;
    e[7] = (n12 * n33 * n41 - n13 * n32 * n41 + n13 * n31 * n42 - n11 * n33 * n42 - n12 * n31 * n43 + n11 * n32 * n43) * d;
    e[8] = t13 * d;
    e[9] = (n14 * n23 * n41 - n13 * n24 * n41 - n14 * n21 * n43 + n11 * n24 * n43 + n13 * n21 * n44 - n11 * n23 * n44) * d;
    e[10] = (n12 * n24 * n41 - n14 * n22 * n41 + n14 * n21 * n42 - n11 * n24 * n42 - n12 * n21 * n44 + n11 * n22 * n44) * d;
    e[11] = (n13 * n22 * n41 - n12 * n23 * n41 - n13 * n21 * n42 + n11 * n23 * n42 + n12 * n21 * n43 - n11 * n22 * n43) * d;
    e[12] = t14 * d;
    e[13] = (n13 * n24 * n31 - n14 * n23 * n31 + n14 * n21 * n33 - n11 * n24 * n33 - n13 * n21 * n34 + n11 * n23 * n34) * d;
    e[14] = (n14 * n22 * n31 - n12 * n24 * n31 - n14 * n21 * n32 + n11 * n24 * n32 + n12 * n21 * n34 - n11 * n22 * n34) * d;
    e[15] = (n12 * n23 * n31 - n13 * n22 * n31 + n13 * n21 * n32 - n11 * n23 * n32 - n12 * n21 * n33 + n11 * n22 * n33) * d;
    return this;
  }
  transpose(): this {
    const e = this.e; let t: number;
    t = e[1]; e[1] = e[4]; e[4] = t;
    t = e[2]; e[2] = e[8]; e[8] = t;
    t = e[6]; e[6] = e[9]; e[9] = t;
    t = e[3]; e[3] = e[12]; e[12] = t;
    t = e[7]; e[7] = e[13]; e[13] = t;
    t = e[11]; e[11] = e[14]; e[14] = t;
    return this;
  }

  transformPoint(v: Vec3, out = v): Vec3 {
    const e = this.e, x = v.x, y = v.y, z = v.z;
    const w = e[3] * x + e[7] * y + e[11] * z + e[15] || 1;
    out.x = (e[0] * x + e[4] * y + e[8] * z + e[12]) / w;
    out.y = (e[1] * x + e[5] * y + e[9] * z + e[13]) / w;
    out.z = (e[2] * x + e[6] * y + e[10] * z + e[14]) / w;
    return out;
  }
  transformDirection(v: Vec3, out = v): Vec3 {
    const e = this.e, x = v.x, y = v.y, z = v.z;
    out.x = e[0] * x + e[4] * y + e[8] * z;
    out.y = e[1] * x + e[5] * y + e[9] * z;
    out.z = e[2] * x + e[6] * y + e[10] * z;
    return out;
  }
  /** Max scale factor along any axis (for bounding sphere transforms). */
  maxScale(): number {
    const e = this.e;
    const sx = e[0] * e[0] + e[1] * e[1] + e[2] * e[2];
    const sy = e[4] * e[4] + e[5] * e[5] + e[6] * e[6];
    const sz = e[8] * e[8] + e[9] * e[9] + e[10] * e[10];
    return Math.sqrt(Math.max(sx, sy, sz));
  }
}
